<template>
    <component
        v-bind="$attrs"
        :is="tagName"
        :target="realTarget"
        :rel="realRel"
        :href="computedHref"
        :title="title"
        :data-has-no-href="hasNoHref"
        :aria-disabled="ariaDisabled"
        :aria-label="ariaLabel || title"
        :aria-describedby="ariaDescribedby"
        @click="handleClick"
        @keydown="handleKeydown"
    >
        <slot :key="$attrs" />
    </component>
</template>

<script>
import analytics from '@core/mixins/analytics.js';
import language from '@core/mixins/language';
import utm from '@core/mixins/utm.js';
import { getElementPath } from '@utils/analytics.ts';
import { normalizeHref } from '@utils/normalize-href.js';
import { appendUtmWebteam } from '@utils/utm-webteam';

export default {
    name: 'ADynamicLink',
    mixins: [analytics, language, utm],

    inheritAttrs: false,

    props: {
        to: {
            type: String,
            default: null,
        },
        utmWebteam: {
            type: String,
            default: null,
        },
        target: {
            type: String,
            default: undefined,
        },
        rel: {
            type: String,
            default: undefined,
        },
        title: {
            type: String,
            default: undefined,
        },
        nativeType: {
            type: String,
            default: 'button',
        },
        hasNoHref: {
            type: Boolean,
            default: false,
        },
        ariaDisabled: {
            type: Boolean,
            default: false,
        },
        ariaLabel: {
            type: String,
            default: undefined,
        },
        ariaDescribedby: {
            type: String,
            default: undefined,
        },
        doNotChangeLocation: {
            type: Boolean,
            default: false,
        },
        event: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['click'],
    computed: {
        computedHref() {
            const vwoHref = this.getVwoOverride('data-vwo-href');
            if (vwoHref) return vwoHref;

            let href;
            if (this.getSessionUTM.length) {
                href = this.setQueryString(this.to || '/') || normalizeHref(this.to, this.pageLocaleMarker);
            } else {
                href = normalizeHref(this.to, this.pageLocaleMarker);
            }

            if (this.utmWebteam && href) {
                return appendUtmWebteam(href, this.utmWebteam, this.$config.domain);
            }
            return href;
        },

        tagName() {
            return this.to ? 'a' : 'button';
        },
        link() {
            return (this.to || '').trim();
        },
        realTarget() {
            const defaultTarget = this.target || '_self';
            const newWindowTarget = '_blank';

            if (!this.isAbsoluteLink()) {
                return defaultTarget;
            }

            return this.isAcronisLink() || this.isAcronisScsLink() ? defaultTarget : newWindowTarget;
        },
        realRel() {
            const defaultRel = this.rel || undefined;
            const noFollow = 'nofollow';

            if (!this.isAbsoluteLink()) {
                return defaultRel;
            }

            return this.isAcronisLink() || this.isAcronisScsLink() ? defaultRel : noFollow;
        },

    },
    methods: {
        isAbsoluteLink() {
            return (
                this.link.startsWith('http:') ||
                this.link.startsWith('https://') ||
                this.link.startsWith('//')
            );
        },
        isAcronisLink() {
            return this.link.split('//').pop().toLowerCase().startsWith('www.acronis.com');
        },
        isAcronisScsLink() {
            return this.link.includes('acronisscs.com');
        },
        handleKeydown(event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (event.key === ' ') {
                    event.target.click();
                }

                this.handleClick(event);
            }
        },
        handleClick(event) {
            const href = this.computedHref || '';

            if (!this.event?.doNotSendGA) {
                // Process GA event
                const incomingGA = this.event;
                const linkTarget = /\.(pdf|zip|pptx)$/.test(href) ? 'documents' : 'link';
                const linkContext = linkTarget === 'link' ? 'click' : 'download';

                const gaData = {
                    event: incomingGA.event || 'Acronis',
                    eventCategory: incomingGA.category || 'Interactions',
                    eventAction: this.getVwoOverride('data-vwo-text') || incomingGA?.action || linkTarget,
                    eventLabel: this.getVwoOverride('data-vwo-href') || incomingGA?.label || href || '',
                    eventContext: incomingGA.context || linkContext,
                    eventContent: this.getVwoOverride('data-vwo-text') || incomingGA?.content || event.target.innerText || '',
                    eventLocation: incomingGA.location || getElementPath(event.target),
                    eventButton: incomingGA.button || '',
                };
                if (incomingGA.formFieldA) gaData.formFieldA = incomingGA.formFieldA;
                if (incomingGA.formFieldB) gaData.formFieldB = incomingGA.formFieldB;
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push(gaData);
            }

            if (this.doNotChangeLocation) {
                event.preventDefault();
                this.$emit('click', event);
                document.activeElement.blur();
                return;
            }

            const runtimeHref = this.$el?.getAttribute?.('href');
            if (runtimeHref && /\b(gclid|fbclid|msclkid|_gl)=/.test(runtimeHref)) {
                event.preventDefault();
                if (this.realTarget === '_blank') {
                    window.open(runtimeHref, '_blank');
                } else {
                    window.location.href = runtimeHref;
                }
                this.$emit('click', event);
                return;
            }

            // handle the link
            if (href) {
                event.preventDefault();

                const isKeyboardHack =
                    this.nativeType === 'a' &&
                    !href.startsWith('tel:') &&
                    (event.metaKey || event.ctrlKey);

                if (isKeyboardHack || this.realTarget === '_blank') {
                    window.open(href, '_blank');
                } else {
                    window.location.href = href;
                }
            }

            this.$emit('click', event);
        },

        getVwoOverride(attrName) {
            if (typeof window === 'undefined') return null;

            const el = this.$el;
            if (el && el.getAttribute) return el.getAttribute(attrName) ?? null;
            return null;
        },
    },
};
</script>
